import { TaskObjectNaming } from "@jugl-web/rest-api/tasks";
import { Alert } from "@jugl-web/ui-components/cross-platform/Alert/Alert";
import { Button } from "@jugl-web/ui-components/cross-platform/Button";
import { PlainButton } from "@jugl-web/ui-components/cross-platform/PlainButton";
import { Radio } from "@jugl-web/ui-components/cross-platform/Radio";
import { TextInput } from "@jugl-web/ui-components/cross-platform/forms/TextInput";
import { MobileDrawer } from "@jugl-web/ui-components/mobile/MobileDrawer";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ManageBoardFormValues } from "../../../../types";
import { ReactComponent as TitleIcon } from "../../assets/gears.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";

const CUSTOM_NAME_MAX_LENGTH = 32;

export const CustomNameAlert = ({
  isFormReadonly,
  params,
  onSave,
  onRadioClickWhenValueIsSet,
}: {
  isFormReadonly: boolean;
  params: ManageBoardFormValues;
  onSave: ({ singular, plural }: TaskObjectNaming) => void;
  onRadioClickWhenValueIsSet: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslations();
  const { isMobile } = useAppVariant();
  const { register, formState, handleSubmit, reset, watch } = useForm({
    defaultValues: params.customTaskObjectNaming,
    mode: "all",
  });

  const onSubmit = (data: TaskObjectNaming) => {
    onSave({ singular: data.singular, plural: data.plural });
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      reset(params.customTaskObjectNaming);
    }
  }, [isOpen, params.customTaskObjectNaming, reset]);

  const singular = watch("singular");
  const plural = watch("plural");

  const inputValue =
    params.customTaskObjectNaming.singular &&
    params.customTaskObjectNaming.plural
      ? `${params.customTaskObjectNaming.singular}, ${params.customTaskObjectNaming.plural}`
      : "";

  const handleRadioClick = () => {
    if (isFormReadonly) return;
    if (inputValue) {
      onRadioClickWhenValueIsSet();
      return;
    }
    setIsOpen(true);
  };

  const title = t({
    id: "tasks-board-form.section.board-settings-custom-name-alert.title",
    defaultMessage: "How to name items in the Board?",
  });

  const saveButtonText = t({
    id: "common.save",
    defaultMessage: "Save",
  });

  const formContent = (
    <div className="flex flex-col gap-6 text-left">
      <TextInput
        autoFocus
        isRequired
        label={t({
          id: "tasks-board-form.section.board-settings-custom-name-alert.singular-label",
          defaultMessage: "Singular form",
        })}
        placeholder={t({
          id: "tasks-board-form.section.board-settings-custom-name-alert.singular-placeholder",
          defaultMessage: "e.g. Template",
        })}
        {...register("singular", {
          validate: (value) =>
            value.trim().length > 0
              ? true
              : t({
                  id: "feedback.mandatory-field",
                  defaultMessage: "Mandatory field",
                }),
          maxLength: {
            value: CUSTOM_NAME_MAX_LENGTH,
            message: `${singular.length}/${CUSTOM_NAME_MAX_LENGTH}`,
          },
        })}
        isInvalid={!!formState.errors.singular}
        errorMessage={formState.errors.singular?.message}
      />
      <TextInput
        label={t({
          id: "tasks-board-form.section.board-settings-custom-name-alert.plural-label",
          defaultMessage: "Plural form",
        })}
        isRequired
        placeholder={t({
          id: "tasks-board-form.section.board-settings-custom-name-alert.plural-placeholder",
          defaultMessage: "e.g. Templates",
        })}
        {...register("plural", {
          validate: (value) =>
            value.trim().length > 0
              ? true
              : t({
                  id: "feedback.mandatory-field",
                  defaultMessage: "Mandatory field",
                }),
          maxLength: {
            value: CUSTOM_NAME_MAX_LENGTH,
            message: `${plural.length}/${CUSTOM_NAME_MAX_LENGTH}`,
          },
        })}
        isInvalid={!!formState.errors.plural}
        errorMessage={formState.errors.plural?.message}
      />
    </div>
  );

  return (
    <>
      <div className="flex h-10 basis-1/2 gap-2">
        <div className="flex h-full items-center">
          <Radio
            isDisabled={isFormReadonly}
            isChecked={params.taskObjectNamingId === "custom"}
            onClick={handleRadioClick}
          />
        </div>
        <PlainButton
          onClick={() => {
            if (isFormReadonly) return;
            setIsOpen(true);
          }}
        >
          <TextInput
            readOnly
            size="small"
            placeholder={t({
              id: "common.custom",
              defaultMessage: "Custom",
            })}
            value={inputValue}
            classNames={{
              endContentWrapperClassName: "bg-transparent",
              wrapperClassName: "w-full max-w-[268px]",
              inputWrapperClassName:
                "focus-within:ring-0 focus-within:border-grey-400",
              inputClassName:
                "w-full max-w-[268px] cursor-pointer focus-within:ring-0 truncate",
            }}
            isDisabled={isFormReadonly}
            endContent={
              params.taskObjectNamingId === "custom" ? (
                <EditIcon />
              ) : (
                <AddIcon />
              )
            }
          />
        </PlainButton>
      </div>
      {isMobile ? (
        <MobileDrawer
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          header={{ title }}
        >
          <MobileDrawer.Content>{formContent}</MobileDrawer.Content>
          <MobileDrawer.Actions>
            <Button
              fullWidth
              color="primary"
              onClick={() => handleSubmit(onSubmit)()}
            >
              {saveButtonText}
            </Button>
          </MobileDrawer.Actions>
        </MobileDrawer>
      ) : (
        <Alert
          className="w-[500px]"
          onRequestClose={() => {
            setIsOpen(false);
          }}
          buttonsContainerClassName="flex items-center justify-center [&>button]:flex-none"
          isOpen={isOpen}
          header={
            <div className="flex items-center gap-2">
              <TitleIcon />
              {title}
            </div>
          }
          isCloseButtonVisible
          buttons={[
            {
              className: "w-[300px]",
              color: "primary",
              onClick: () => {
                handleSubmit(onSubmit)();
              },
              text: saveButtonText,
            },
          ]}
          content={formContent}
        />
      )}
    </>
  );
};
